import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

// ---------------------------------------------------------------------------
// AiNarrativeBlock — a reusable "click for AI narrative" strip.
//
// The parent component supplies:
//   - kind:      'analyze_student' | 'suggest_interventions' | 'parent_email'
//   - label:     display label for the button
//   - hint:      one-line description shown next to the button
//   - fetcher:   () => Promise<string>
//
// This component owns the loading / error / result state so the parent
// can drop several of these into the same page without any shared
// wiring. The result renders as a fenced <pre> so line breaks (bullet
// lists, paragraphs) come out the way the LLM formatted them.
//
// If the LLM path is unavailable (err.__unavailable), we show a subdued
// "AI narrative isn't set up yet" note rather than a red error box.
// ---------------------------------------------------------------------------

const AiNarrativeBlock = ({ kind, label, hint, iconName, fetcher, onCopyToClipboard }) => {
  const [loading, setLoading] = useState(false);
  const [text, setText] = useState('');
  const [error, setError] = useState(null);
  const [unavailable, setUnavailable] = useState(false);

  const run = async () => {
    setLoading(true);
    setError(null);
    setUnavailable(false);
    try {
      const result = await fetcher();
      setText(result || '');
    } catch (err) {
      if (err?.__unavailable) {
        setUnavailable(true);
        setError(err.message || 'AI narrative isn\'t set up yet.');
      } else {
        setError(err?.message || 'AI narrative failed.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      if (onCopyToClipboard) onCopyToClipboard();
    } catch {
      // clipboard blocked — nothing to do
    }
  };

  return (
    <div className="border border-slate-200 rounded-lg p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <Icon name={iconName || 'Sparkles'} size={16} className="text-indigo-600" />
            <h4 className="font-medium text-sm text-slate-900">{label}</h4>
          </div>
          {hint && <p className="text-xs text-slate-500">{hint}</p>}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {text && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCopy}
              iconName="Copy"
              iconPosition="left"
            >
              Copy
            </Button>
          )}
          <Button
            variant={text ? 'ghost' : 'default'}
            size="sm"
            onClick={run}
            loading={loading}
            iconName={text ? 'RefreshCw' : 'Sparkles'}
            iconPosition="left"
          >
            {text ? 'Regenerate' : 'Generate'}
          </Button>
        </div>
      </div>

      {loading && (
        <div className="mt-3 text-xs text-slate-500">
          Talking to the AI…
        </div>
      )}

      {unavailable && !loading && (
        <div className="mt-3 rounded bg-slate-50 border border-slate-200 p-3">
          <div className="flex items-start gap-2 text-slate-700 text-xs">
            <Icon name="Info" size={14} className="text-slate-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium mb-0.5">AI narrative not set up yet</p>
              <p className="opacity-80">{error}</p>
              <p className="opacity-80 mt-1">
                All the deterministic insights above are still live from your own
                data. Ask your admin to add <code className="font-mono text-[11px]">GEMINI_API_KEY</code>{' '}
                to the Supabase Edge Function to enable AI-written narratives.
              </p>
            </div>
          </div>
        </div>
      )}

      {error && !unavailable && !loading && (
        <div className="mt-3 rounded bg-red-50 border border-red-200 p-3 text-xs text-red-700">
          {error}
        </div>
      )}

      {text && !loading && (
        <div className="mt-3 rounded bg-indigo-50 border border-indigo-100 p-3 text-sm text-slate-800 whitespace-pre-wrap leading-relaxed">
          {text}
        </div>
      )}
    </div>
  );
};

export default AiNarrativeBlock;
